// Deterministic product title + meta description builders.
// Single source of truth: imported by scripts/prerender-og.mjs (build time) and
// by src/lib/productMeta.ts (runtime), so the crawler-facing head and the
// hydrated head can never drift apart. No LLM, no per-product authoring.

import {
  COLORS,
  COLOR_PREFIXES,
  COMPOUND_COLORS,
  MATERIAL_PREFIXES,
  MEANING_PREFIXES,
  MATERIAL_WORDS,
} from "./title-qualifiers.mjs";

const VENDOR_NAMES =
  /\b(HomeRoots|Home Roots|HomeSullivan|Home Sullivan|Furniture of America|Baxton Studio|Modway|Flash Furniture|FurniFindUSA|FurniFind|Furnifind)\b/gi;

export const SUFFIX = " \u2014 LoftLoom";
export const TITLE_BARE_MAX = 60;
export const TITLE_WITH_SUFFIX_MAX = 49;
export const TITLE_HARD_MAX = 65;
export const DESCRIPTION_MAX = 155;

/**
 * Round 37, FIX 2. Two live supplier titles carry chemical nomenclature —
 * `p-Oxybis(benzenesulfonyl hydrazide)` — inside an otherwise normal product
 * name. Strip the parenthetical AND the orphaned `p-`/`o-`/`m-` chemical word.
 * Deliberately keyword-gated so real materials survive: `Magnesium Oxide` in
 * "32000 BTU 26\" Gray Magnesium Oxide Propane Round Fire Pit" is a genuine
 * fire-pit material and must never be touched.
 */
const CHEMICAL_WORD = /(?:oxybis|benzenesulfon\w*|sulfonyl|sulphonyl|hydrazide)/i;

export function stripChemicalNames(text) {
  let s = String(text ?? "");
  // 1. parentheticals whose contents are chemical nomenclature
  s = s.replace(/\s*\(([^)]*)\)/g, (m, inner) => (CHEMICAL_WORD.test(inner) ? " " : m));
  // 2. the orphaned chemical word, with any p-/o-/m- positional prefix
  s = s.replace(
    /\s*\b(?:[pom]-)?[A-Za-z]*(?:oxybis|benzenesulfon[a-z]*|sulfonyl|sulphonyl|hydrazide)[A-Za-z]*\b/gi,
    " ",
  );
  return s;
}

/**
 * Round 37, FIX 3. Supplier feeds double a product noun back to back
 * ("Throw Throw Blanket", "Desk Desk and Bookshelf"). Collapse ONLY when the
 * repeated word is a product noun — never a colour that closes one phrase and
 * opens the next ("Green and Red Red Truck", "Tan And Black Black Leg").
 */
export const STUTTER_NOUNS = new Set([
  "throw", "blanket", "desk", "lamp", "table", "chair", "sofa", "boat", "shelf", "bookshelf",
  "rug", "mirror", "bench", "stand", "tray", "frame", "cabinet", "dresser", "sculpture",
  "vase", "pot", "planter", "stool", "sconce", "shade", "pillow", "cushion", "ottoman",
  "chandelier", "pendant", "basket", "bowl", "clock", "curtain", "quilt", "comforter",
  "duvet", "mat", "doormat", "wreath", "candle", "holder", "bed", "nightstand", "console",
  "cart", "rack", "hook", "sign", "figurine", "statue", "bin", "box", "case", "drawer",
]);

export function collapseStutters(text) {
  return String(text ?? "").replace(/\b(\w+)(\s+)\1\b/gi, (m, word, gap) =>
    STUTTER_NOUNS.has(word.toLowerCase()) ? word : m,
  );
}

/**
 * Round 38, FIX 2. Accent-pillow titles end in a raw supplier spec block —
 * `5.0" (L) x 18.0" (W) x 18.0" (H)`. It is noise in a <title>, and cutting the
 * title to 60 chars around it was dropping the product's own colour. Removed
 * whole; a LEADING size such as `18" X 18"` is untouched, and no colour or
 * material word can be inside the block (it is digits, quotes and L/W/H/D only).
 */
const SPEC_BLOCK =
  /\s*\d+(?:\.\d+)?\s*(?:"|''|in\.?|inch(?:es)?)?\s*\(\s*[LWHD]\s*\)(?:\s*[xX×]\s*\d+(?:\.\d+)?\s*(?:"|''|in\.?|inch(?:es)?)?\s*\(\s*[LWHD]\s*\))+\s*$/;

export function stripSpecBlock(text) {
  return String(text ?? "").replace(SPEC_BLOCK, "");
}

/**
 * A supplier title with machine markup left in it: a ";" list, or a bracket
 * that never closes ("17" White Plastic repalce(Manual,Non-Electrical;Electric;
 * …) Oval One-Piece Toilet"). The shortener drops what it cannot read and keeps
 * the supplier type's noun, so four toilet SEATS shipped the clean, confident
 * name "17" White Plastic Toilet". No name built from such a title can be
 * trusted: the line is delisted (scripts/data/delisted-titles.json) and
 * analyze-titles.mjs fails the build on a new one.
 */
export function isGarbledTitle(title) {
  const t = String(title ?? "");
  return t.includes(";") || (t.match(/\(/g) ?? []).length !== (t.match(/\)/g) ?? []).length;
}

export function stripVendors(text) {
  return collapseStutters(stripSpecBlock(stripChemicalNames(String(text ?? "").replace(VENDOR_NAMES, ""))))
    .replace(/\s{2,}/g, " ")
    .replace(/\s+([,.!?])/g, "$1")
    .replace(/[\s\-\u2013\u2014|,]+$/, "")
    .trim();
}


/**
 * Leading supplier dimension prefix, e.g. `1.5" X 1.5" X 1.5" ` or `10" `.
 * Returns { dimensions, rest }. Never removes anything that isn't numeric:
 * a bare number with no inch mark and no `x` chain is left alone (it could be a
 * pack count or a model number).
 */
const DIM_PREFIX =
  /^((?:\d+(?:\.\d+)?\s*(?:"|''|in\.?|inch)?\s*(?:x|X|\u00d7)\s*)+\d+(?:\.\d+)?\s*(?:"|''|in\.?|inch)?|\d+(?:\.\d+)?\s*(?:"|''|inch))\s+(?=\S)/;

/**
 * The leading size a shopper picks by: one measure (`36"`) or a pair (`24" X 36"`).
 * A W x D x H triple is the overall box — it is spent first, as it always was.
 */
export function sellingSizePrefix(title) {
  const { dimensions } = splitDimensionPrefix(title);
  return dimensions && (dimensions.match(/\d+(?:\.\d+)?/g) ?? []).length < 3 ? dimensions : "";
}

export function splitDimensionPrefix(title) {
  const t = String(title ?? "").trim();
  const m = t.match(DIM_PREFIX);
  if (m) return { dimensions: m[1].replace(/\s+/g, " ").trim(), rest: t.slice(m[0].length).trim() };
  return { dimensions: "", rest: t };
}

/**
 * Round 39. The old shortener cut heads at ` and `/` in `/` for ` and shaved
 * leading words one at a time, so 3,709 titles lost the words that describe the
 * product: the quantity ("Set of Four"), the lead colour ("Teal Blue" → "Blue"),
 * the material, or the product noun itself. The over-60 path is now built around
 * PROTECTED TOKENS, which are never removed and never split.
 *
 * ONLY these open an accessory clause. ` and `, ` in `, ` for ` are NOT clause
 * boundaries in these feeds — `and` joins colours and materials INSIDE the
 * product phrase. (One exception, decided by the supplier's product type: an
 * add-on straight after the product noun — see clauseStart.)
 */
const CLAUSE_OPENERS_RE = /(?:,|\s(?:with|featuring|including)\s)/gi;
const LEADING_JUNK = /^(?:and|with|for|in|plus|or)\s+/i;

const tidy = (s) =>
  String(s ?? "")
    .replace(/\s{2,}/g, " ")
    .replace(/[\s,;\-\u2013\u2014]+$/, "")
    .replace(/\s+\b(?:and|or|with|plus)$/i, "")
    .replace(LEADING_JUNK, "")
    .trim();

/**
 * The supplier's own word for what the product is, read off its product type:
 * "Outdoor Planters" → planter, "Throws & Blankets" → throw + blanket, "Love
 * Seats" → seat + loveseat. The shortener treated the LAST word of a title as
 * the product noun, which is wrong whenever something trails it — a sofa sold
 * "And Toss Pillows" shipped an <h1> ending "Sleeper Pillows", a "Planter Box"
 * became a "Box". The supplier's type settles it, so its noun is never removed.
 */
export const nounStem = (w) =>
  String(w ?? "")
    .toLowerCase()
    .replace(/[^a-z]/g, "")
    .replace(/ies$/, "y")
    .replace(/ves$/, "f")
    .replace(/(ss|x|z|ch|sh)es$/, "$1")
    .replace(/([^s])s$/, "$1");

const typeNounCache = new Map();
export function typeNouns(productType) {
  const key = String(productType ?? "");
  let nouns = typeNounCache.get(key);
  if (!nouns) {
    const parts = key.split(/\s*(?:&|,|\/|-|\band\b)\s*/i).map((part) => part.trim().split(/\s+/)).filter((w) => w[0]);
    const last = parts[parts.length - 1] ?? [];
    nouns = new Set();
    for (const words of parts) {
      const head = words[words.length - 1];
      // An earlier part is often only a modifier ("Bar & Restaurant Tables",
      // "Foam, Spring And Hybrid Mattresses"); a plural marks a real noun.
      if (words === last || /s$/i.test(head)) nouns.add(nounStem(head));
    }
    // "Love Seats" is written "Loveseat" in titles, "Lampshades" as "Lamp Shade".
    if (last.length > 1) nouns.add(nounStem(last.slice(-2).join("")));
    for (const w of [...nouns]) if (w.length < 3) nouns.delete(w);
    // A type with one noun names one thing, so what follows that noun is an add-on.
    nouns.single = parts.filter((words) => words === last || /s$/i.test(words[words.length - 1])).length === 1;
    typeNounCache.set(key, nouns);
  }
  return nouns;
}

/**
 * 1 when words[i] is the supplier type's noun, 2 when it closes a two-word
 * spelling of it, else 0. "4-Light" is a count, not the noun "Light".
 */
const isTypeNoun = (words, i, nouns) =>
  /\d/.test(words[i]) ? 0 : i > 0 && nouns.has(nounStem(`${words[i - 1]}${words[i]}`)) ? 2 : nouns.has(nounStem(words[i])) ? 1 : 0;

const ADD_ON_RE = /^(?:and|&|plus|in)$/i;

/**
 * Where the accessory clause starts, or -1. Normally the first "with": "Table
 * Lamp With White Shade". Two supplier habits break that, and the supplier
 * type's noun settles both:
 *  - the product noun comes AFTER the "with" ("Everyday Starts with Coffee 4
 *    Black Framed Print Wall Art", "Glass And Metal With Iron Round Coffee
 *    Table") — cutting there left a title with no product in it. A "with" is
 *    skipped when the type's noun has not appeared yet and the phrase it opens
 *    closes on that noun;
 *  - an add-on follows the noun without a "with" ("Sofa And Toss Pillows",
 *    "Candle In White Resin Holder") — it is a clause too.
 */
function clauseStart(rest, nouns) {
  const openers = [...rest.matchAll(CLAUSE_OPENERS_RE)].map((m) => m.index);
  if (!nouns?.size) return openers.length ? openers[0] : -1;

  const tokens = [...rest.matchAll(/\S+/g)];
  const words = tokens.map((t) => t[0]);
  // The noun still to come closes a phrase: the last word before an opener, an
  // add-on or the end ("Cabinet With Storage Shelves" does not count).
  const closesLater = (from) =>
    tokens.some((t, k) => {
      if (t.index < from || !isTypeNoun(words, k, nouns)) return false;
      const next = tokens[k + 1];
      return !next || ADD_ON_RE.test(next[0]) || openers.some((o) => o >= t.index && o < next.index);
    });

  let cut = -1;
  for (const at of openers) {
    const seen = tokens.some((t, k) => t.index < at && isTypeNoun(words, k, nouns));
    if (seen || !closesLater(at)) {
      cut = at;
      break;
    }
  }
  if (nouns.single) {
    for (let k = 0; k < tokens.length - 1; k++) {
      if (cut >= 0 && tokens[k].index >= cut) break;
      if (isTypeNoun(words, k, nouns) && ADD_ON_RE.test(words[k + 1])) return tokens[k].index + words[k].length;
    }
  }
  return cut;
}

/** The product phrase of a title: everything before its accessory clause. */
export function productPhrase(title, productType = "") {
  const name = String(title ?? "").trim();
  const cut = clauseStart(name, typeNouns(productType));
  return (cut >= 0 ? name.slice(0, cut) : name).trim();
}

const NUMBER_WORDS = {
  one: "1", two: "2", three: "3", four: "4", five: "5", six: "6", seven: "7",
  eight: "8", nine: "9", ten: "10", eleven: "11", twelve: "12",
};

const COLOR_WORD_RE = new RegExp(`^(?:${COLORS})$`, "i");
const COLOR_PREFIX_RE = new RegExp(`^(?:${COLOR_PREFIXES})$`, "i");
const MATERIAL_WORD_RE = new RegExp(`^(?:${MATERIAL_WORDS.replace(/\|Mango Wood/, "")})$`, "i");
const MATERIAL_PREFIX_RE = new RegExp(`^(?:${MATERIAL_PREFIXES})$`, "i");
/**
 * Round 41, FIX 1. A prefix that changes what the material IS ("Faux Leather",
 * "Manufactured Wood") is part of the material token: never dropped on its own.
 */
export const MEANING_PREFIX_RE = new RegExp(`^(?:${MEANING_PREFIXES})$`, "i");

/** Every material word, for the round-40 "material swapped" audit. */
export const MATERIAL_SCAN_RE = new RegExp(
  `\\b(?:${MATERIAL_WORDS.replace(/\|Mango Wood/, "")})\\b`,
  "gi",
);

/**
 * Round 40, FIX 1 context guard. These words are MATERIALS in most titles but
 * PRODUCT NOUNS inside the noun phrase ("Wall Mirror", "Canvas Wall Art").
 * Materials are never marked inside the noun phrase, so the guard is structural.
 */
const NOUN_ONLY_MATERIALS = new Set(["mirror", "mirrored", "canvas"]);

/** Modifiers that change what the product DOES — never filler. */
const FUNCTIONAL = new Set([
  "reclining", "recliner", "swivel", "adjustable", "outdoor", "convertible", "folding",
  "sleeper", "extendable", "rotating", "tilting", "motorized", "electric", "manual",
  "power", "storage", "stackable", "rocking", "gliding", "sectional", "corner",
  "counter", "bar", "height", "waterproof", "portable", "wall", "mounted",
  // Round 41, FIX 2: "Rolling" is what an office chair DOES.
  "hanging", "freestanding", "zippered", "removable", "rolling",
]);

/**
 * Round 40, FIX 2. Configuration words say what the product IS — a U-shaped
 * ten-piece sectional is not interchangeable with a sectional. Protected
 * alongside the functional modifiers, and given up only after them.
 */
const CONFIG_WORDS = new Set(["modular", "sleeper", "convertible", "shaped", "reversible"]);
const CONFIG_LETTER_RE = /^[ULJ]$/;

/**
 * Two-word product types that must never be reduced to their head noun — the
 * modifier is part of the type a shopper searches for.
 */
const WHOLE_NOUN_TYPES = new Set([
  "bar chair", "bar chairs", "bar stool", "bar stools", "area rug", "area rugs",
  "floor lamp", "floor lamps", "table lamp", "table lamps", "coffee table", "coffee tables",
  "side chair", "side chairs", "side table", "side tables", "dining chair", "dining chairs",
  "throw blanket", "throw blankets", "throw pillow", "throw pillows", "wall art",
  "wall mirror", "wall mirrors", "picture frame", "picture frames", "console table",
  "console tables", "end table", "end tables", "dining table", "dining tables",
  "night stand", "night stands", "nightstand", "bed frame", "bed frames",
  "pendant light", "pendant lights", "ceiling fan", "ceiling fans", "office chair",
  "office chairs", "accent chair", "accent chairs", "fire pit", "fire pits",
  "removable tiles", "candle holder", "candle holders", "wall sconce", "wall sconces",
  "storage cabinet", "storage cabinets", "tv stand", "tv stands", "bar cart", "bar carts",
  "serving tray", "serving trays", "flower pot", "flower pots", "pot planter",
  "hand towel", "hand towels", "bath sheet", "bath sheets", "bath towel", "bath towels",
  "medicine cabinet", "medicine cabinets", "wine refrigerator", "wine refrigerators",
  "beverage refrigerator", "beverage refrigerators", "wine cooler", "wine coolers",
]);


/**
 * Step 4. Low-information filler that restates the category. Chosen by
 * measuring the words that appear in the over-60 titles and carry no
 * distinguishing meaning (see scripts/measure-title-filler.mjs output in the
 * round-39 report). Multi-word entries are matched as adjacent runs.
 */
export const FILLER_PHRASES = [
  "indoor outdoor", "indoor/outdoor", "all purpose", "all weather", "hand painted",
  "hand carved", "hand crafted", "handcrafted", "heavy duty", "high quality",
  "multi purpose", "easy care", "low back", "high back", "no textural detailing",
];
export const FILLER_WORDS = new Set([
  "washable", "dimmable", "tabletop", "decorative", "traditional", "transitional",
  "classic", "premium", "deluxe", "luxury", "stylish", "elegant", "beautiful",
  "durable", "sturdy", "standard", "regular", "assorted", "plush", "soft", "cozy",
]);

const QTY_HEAD_RE = /^(?:set|pair|pack|group|lot)$/i;

/** A bed size says which product this is ("Queen", "Split California King", "Twin XL", "Twin Over Full"). */
const SLEEP_NOUN_RE = /^(?:beds?|mattress(?:es)?|headboards?|daybeds?|bunk|futons?)$/;
const BED_SIZE_WORD_RE = /^(?:twin|full|queen|king)$/;

/** A count that belongs to the next word: "Six Panel", "Three Leg", "Single Door". */
const COUNT_WORD_RE = /^(?:one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve|single|double|dual|triple)$/;
/**
 * Two free words that name one thing. Not "Framed Print": there the print is
 * the product, and "Framed Wall Art" without it still says so.
 */
const BOUND_PAIR_RE = /^(?:down alternative|(?!(?:un)?framed? |canvas |art )[a-z-]+ print)$/;

/** Tokenise the product part and mark every token's role. */
function annotate(productPart, nouns) {
  const words = productPart.split(/\s+/).filter(Boolean);
  const roles = words.map(() => "free");

  const lower = words.map((w) => w.toLowerCase().replace(/[^a-z0-9'".%\-\/]/g, ""));

  // --- leading size run: never split (a "2' X 5'" must not become "2' X") ----
  const MEASURE_TOKEN = /^[\d.]+(?:"|''|'|in\.?|inch(?:es)?|ft\.?|feet|cm)?$/i;
  const X_TOKEN = /^[xX\u00d7]$/;
  let runEnd = -1;
  let hasUnitOrX = false;
  for (let i = 0; i < words.length; i++) {
    if (X_TOKEN.test(words[i])) { hasUnitOrX = true; runEnd = i; continue; }
    if (!MEASURE_TOKEN.test(words[i])) break;
    if (/["'a-z]/i.test(words[i])) hasUnitOrX = true;
    runEnd = i;
  }
  if (hasUnitOrX) {
    while (runEnd >= 0 && X_TOKEN.test(words[runEnd])) runEnd--;
    for (let i = 0; i <= runEnd; i++) roles[i] = "size";
  }
  // A size further in (`27" X 54"` before "Bath Towels", `23"` after "Set Of
  // Two") is the same kind of token group: spent one word at a time as a
  // descriptor it shipped `27" X Bath Towels`. A lone bare number is not a size
  // — it is a count or a series number ("… Home 1 Black Framed Print").
  for (let i = hasUnitOrX ? runEnd + 1 : 0; i < words.length; i++) {
    if (roles[i] !== "free" || !MEASURE_TOKEN.test(words[i])) continue;
    let end = i;
    while (X_TOKEN.test(words[end + 1] ?? "") && MEASURE_TOKEN.test(words[end + 2] ?? "")) end += 2;
    if (end === i && !/["'a-z]/i.test(words[i])) continue;
    for (let k = i; k <= end; k++) roles[k] = "size";
    i = end;
  }

  // --- a quoted name ("Together Is Our Favorite Place to Be!"): kept or dropped whole
  for (let i = 0; i < words.length; i++) {
    if (roles[i] !== "free" || !/^["\u201c]/.test(words[i]) || /^["\u201c][^"\u201d]*["\u201d][,.!?]*$/.test(words[i])) continue;
    const end = words.findIndex((w, k) => k > i && /["\u201d][,.!?]*$/.test(w));
    if (end < 0) break;
    for (let k = i; k <= end; k++) if (roles[k] === "free") roles[k] = "quoted";
    i = end;
  }

  // --- filler phrases (adjacent runs) --------------------------------------
  for (const phrase of FILLER_PHRASES) {
    const parts = phrase.split(" ");
    for (let i = 0; i + parts.length <= words.length; i++) {
      if (parts.every((p, k) => lower[i + k] === p) && parts.every((_p, k) => roles[i + k] === "free")) {
        for (let k = 0; k < parts.length; k++) roles[i + k] = "filler";
      }
    }
  }
  for (let i = 0; i < words.length; i++) {
    if (roles[i] === "free" && FILLER_WORDS.has(lower[i])) roles[i] = "filler";
  }

  // --- quantity phrase ------------------------------------------------------
  const qty = [];
  for (let i = 0; i < words.length; i++) {
    // "Set of Two", "Pair of", "Pack of 4"
    if (QTY_HEAD_RE.test(lower[i])) {
      const run = [i];
      if (lower[i + 1] === "of") run.push(i + 1);
      const nIdx = run[run.length - 1] + 1;
      if (NUMBER_WORDS[lower[nIdx]] || /^\d+$/.test(lower[nIdx] ?? "")) run.push(nIdx);
      if (run.length >= 2) qty.push(...run);
    }
    // "Ten Piece", "3 Piece"
    if (/^pieces?$/.test(lower[i]) && i > 0 && (NUMBER_WORDS[lower[i - 1]] || /^\d+$/.test(lower[i - 1]))) {
      qty.push(i - 1, i);
    }
  }
  for (const i of qty) roles[i] = "qty";

  // --- bed size: protected, and never read as the noun's modifier ------------
  if (lower.some((w) => SLEEP_NOUN_RE.test(w))) {
    for (let i = 0; i < words.length; i++) {
      if (roles[i] !== "free" || !BED_SIZE_WORD_RE.test(lower[i])) continue;
      roles[i] = "bedSize";
      let first = i;
      if (lower[i] === "king" && lower[i - 1] === "california" && roles[i - 1] === "free") roles[(first = i - 1)] = "bedSize";
      if (lower[first - 1] === "split" && roles[first - 1] === "free") roles[first - 1] = "bedSize";
      if (lower[i] === "twin" && lower[i + 1] === "xl" && roles[i + 1] === "free") roles[i + 1] = "bedSize";
    }
    for (let i = 1; i + 1 < words.length; i++) {
      if (lower[i] === "over" && roles[i] === "free" && roles[i - 1] === "bedSize" && roles[i + 1] === "bedSize") roles[i] = "bedSize";
    }
  }

  // --- lead colour phrase (compound colours atomic) -------------------------
  let leadColorEnd = -1;
  const colorRuns = [];
  for (let i = 0; i < words.length; i++) {
    if (roles[i] !== "free") continue;
    let run = null;
    let compound = false;
    const two = `${lower[i]} ${lower[i + 1] ?? ""}`.trim();
    if (COMPOUND_COLORS.includes(two)) { run = [i, i + 1]; compound = true; }
    else if (COLOR_PREFIX_RE.test(lower[i]) && COLOR_WORD_RE.test(lower[i + 1] ?? "")) run = [i, i + 1];
    else if (COLOR_WORD_RE.test(lower[i])) run = [i];
    if (!run) continue;
    if (run.some((k) => roles[k] !== "free")) continue;
    run.compound = compound;
    colorRuns.push(run);
    i = run[run.length - 1];
  }
  if (colorRuns.length) {
    for (const k of colorRuns[0]) roles[k] = "color1";
    leadColorEnd = colorRuns[0][colorRuns[0].length - 1];
    if (colorRuns[1]) for (const k of colorRuns[1]) roles[k] = colorRuns[1].compound ? "colorCompound" : "color2";
    for (const run of colorRuns.slice(2)) {
      for (const k of run) roles[k] = run.compound ? "colorCompound" : "colorExtra";
    }
  }

  // --- product noun phrase: the last words of the product part --------------
  // Round 40: computed BEFORE materials, so a material word that is really the
  // product noun ("Wall Mirror", "Canvas Wall Art") can never take the
  // "first material" slot away from the real material.
  const nounStart = Math.max(0, words.length - 2);
  const priorRoles = [...roles];
  for (let i = nounStart; i < words.length; i++) roles[i] = "noun";
  // A colour or the quantity must not be swallowed by the noun phrase — round 40:
  // "Twelve Piece Sectional" must not turn the piece count into a noun modifier.
  if (leadColorEnd >= nounStart) for (const k of colorRuns[0]) if (k >= nounStart) roles[k] = "noun";
  for (let i = nounStart; i < words.length - 1; i++) if (priorRoles[i] === "qty" || priorRoles[i] === "bedSize") roles[i] = priorRoles[i];
  // The supplier type's noun is the product noun wherever it stands ("Sofa" in
  // "Sofa And Toss Pillows"): protected like the noun phrase, and never one of
  // the modifiers step 7 trims.
  const typeNounAt = new Set();
  // So is a modifier that is half of an "and": "Salt And Pepper Shakers",
  // "Decanter And Whiskey Glass". Trimmed like any other modifier it left a
  // "Salt Shakers" set and a "Decanter Glass", so all four words are the noun.
  // ("Or" offers a choice — "Pantry Or Storage Closet" — half of it is still
  // true; and two materials are not a noun: "Wood And Glass Sideboard".)
  const andHalf = (i) => roles[i] !== "qty" && !MATERIAL_WORD_RE.test(lower[i]) && !COLOR_WORD_RE.test(lower[i]);
  if (
    nounStart >= 2 &&
    /^(?:and|&|plus)$/i.test(words[nounStart - 1]) &&
    roles[nounStart - 2] === "free" &&
    andHalf(nounStart - 2) &&
    andHalf(nounStart)
  ) {
    for (let i = nounStart - 2; i <= nounStart; i++) {
      roles[i] = "noun";
      typeNounAt.add(i);
    }
  }
  if (nouns?.size) {
    for (let i = 0; i < words.length; i++) {
      const span = isTypeNoun(words, i, nouns);
      for (let k = i - span + 1; span && k <= i; k++) {
        if (roles[k] !== "free" && roles[k] !== "noun") continue;
        roles[k] = "noun";
        typeNounAt.add(k);
      }
    }
  }


  // --- first material (never inside the noun phrase) -------------------------
  let matDone = false;
  // "100% Cotton" is the supplier stating the material: it is THE material, kept
  // whole, and a fabric word before it ("White And Linen 100% Cotton") is a colour.
  for (let i = 0; i + 1 < nounStart && !matDone; i++) {
    if (lower[i] !== "100%" || roles[i] !== "free" || roles[i + 1] !== "free" || !MATERIAL_WORD_RE.test(lower[i + 1])) continue;
    roles[i] = roles[i + 1] = "material1";
    matDone = true;
  }
  for (let i = 0; i < nounStart; i++) {
    if (roles[i] !== "free") continue;
    if (!MATERIAL_WORD_RE.test(lower[i])) continue;
    if (NOUN_ONLY_MATERIALS.has(lower[i])) continue;
    // Round 41, FIX 3: walk the WHOLE prefix run backwards ("Solid Manufactured
    // Wood"), so a prefix can never be left orphaned by its material's removal.
    const run = [i];
    const prefixes = [];
    for (let j = i - 1; j >= 0; j--) {
      if (roles[j] !== "free") break;
      // A noun-only word IS a prefix in this position ("Mirrored Glass"), so the
      // noun guard applies to the material slot only.
      if (!MATERIAL_PREFIX_RE.test(lower[j])) break;
      prefixes.unshift(j);
      run.unshift(j);
    }
    // "Cotton Blend", "Leather Match", "Polyester And Cotton Blend": the word
    // that says the material is not pure stays or leaves with it. Spent apart
    // they shipped a "Polyester Blend" pillow and a "Tufted Blend" bed.
    let end = i;
    if (/^(?:and|&)$/i.test(words[end + 1] ?? "") && roles[end + 1] === "free" && roles[end + 2] === "free" && MATERIAL_WORD_RE.test(lower[end + 2] ?? "")) end += 2;
    if (/^(?:blend|match)$/.test(lower[end + 1] ?? "") && roles[end + 1] === "free" && end + 1 < nounStart) {
      for (let k = i + 1; k <= end + 1; k++) run.push(k);
    }
    for (const k of run) roles[k] = matDone ? "materialExtra" : "material1";
    // Round 41, FIX 1. Only a provenance/finish prefix is spent in overflow
    // ("Italian Leather" → "Leather"); a meaning-bearing prefix ("Faux",
    // "Manufactured") stays part of the material token and keeps role material1.
    if (!matDone) {
      for (const p of prefixes) {
        if (!MEANING_PREFIX_RE.test(lower[p])) roles[p] = "matPrefix";
        else break; // a shrinkable prefix INSIDE the meaning pair stays too
      }
    }
    matDone = true;
  }


  // --- configuration words ("U Shaped", "Modular", "Sleeper") ---------------
  for (let i = 0; i < words.length; i++) {
    if (roles[i] !== "free") continue;
    if (CONFIG_WORDS.has(lower[i])) {
      roles[i] = "config";
      if (i > 0 && roles[i - 1] === "free" && CONFIG_LETTER_RE.test(words[i - 1])) roles[i - 1] = "config";
    }
  }

  // --- functional modifiers -------------------------------------------------
  for (let i = 0; i < words.length; i++) {
    if (roles[i] === "free" && FUNCTIONAL.has(lower[i])) roles[i] = "functional";
  }

  // --- pairs that only mean something whole ---------------------------------
  // bound[i] is the word that leaves with words[i]. Spent one at a time, "Six
  // Panel" left a "Six Outdoor Privacy Screen", "Three Leg Console Table" a
  // "Three Console Table" (one table) and "Down Alternative" a "Down Duvet
  // Insert" — a different filling. The second half may also be the noun
  // phrase's modifier, which step 7 trims ("Two Drawer Nightstand").
  const bound = words.map(() => -1);
  const bind = (a, b) => {
    bound[a] = b;
    bound[b] = a;
  };
  const pairsWith = (j) =>
    bound[j] < 0 && (roles[j] === "free" || (roles[j] === "noun" && j + 1 < words.length && !typeNounAt.has(j)));
  for (let i = 0; i + 1 < words.length; i++) {
    if (roles[i] !== "free" || bound[i] >= 0) continue;
    const count = COUNT_WORD_RE.test(lower[i]);
    // "Service For Four" counts people: the count belongs to its "for".
    if (count && lower[i - 1] === "for" && roles[i - 1] === "free" && bound[i - 1] < 0) bind(i - 1, i);
    else if ((count || BOUND_PAIR_RE.test(`${lower[i]} ${lower[i + 1]}`)) && pairsWith(i + 1)) bind(i, i + 1);
  }

  return { words, roles, colorCount: colorRuns.length, nounStart, typeNounAt, bound };

}

const PROTECTED = new Set([
  "qty", "color1", "colorCompound", "material1", "matPrefix", "functional", "config", "quoted", "bedSize", "noun",
]);

/**
 * Round 40. The classes counted when comparing two candidates for shopper value.
 * The material PREFIX and the noun-phrase MODIFIER are deliberately excluded:
 * they are the two things the ladder is designed to spend first, so giving them
 * up must never look like a loss ("Italian Leather" → "Leather",
 * "Corner Sectional" → "Sectional").
 */
const KEPT_CLASSES = new Set(["quoted", "qty", "color1", "colorCompound", "material1", "functional", "config", "bedSize"]);


const SPENDABLE_HUES = new Set(["color2", "colorExtra", "materialExtra"]);

// "with" only reaches the product part when clauseStart() kept it there.
const CONJUNCTION_RE = /^(?:and|&|or|plus|with|\/)$/i;
const isConjunction = (w) => CONJUNCTION_RE.test(String(w ?? "").replace(/[^A-Za-z&\/]/g, ""));

function render(words, roles, keep, dims, clause) {
  const out = [];
  for (let i = 0; i < words.length; i++) {
    if (!keep[i]) continue;
    // A conjunction whose neighbour was removed would read as "Black Gray And
    // Peel And Stick" — drop it with the word it was joining.
    if (isConjunction(words[i])) {
      const prev = keep[i - 1] === true;
      const next = keep[i + 1] === true;
      if (!prev || !next) continue;
    }
    // "Panda, Bird, Bear, Monkey" cut after "Bear" must not end "Bear,".
    out.push(keep[i + 1] === true ? words[i] : words[i].replace(/,$/, ""));
  }
  const name = tidy([dims, out.join(" ")].filter(Boolean).join(" "));
  return tidy(`${name}${clause}`);
}

function compactQuantity(words, roles) {
  return words.map((w, i) => {
    if (roles[i] !== "qty") return w;
    const key = w.toLowerCase().replace(/[^a-z]/g, "");
    if (key === "of" || QTY_HEAD_RE.test(key)) return w;
    return NUMBER_WORDS[key] ?? w;
  });
}

/**
 * A vanity's "With White Ceramic Sink" clause says a sink is INCLUDED, which
 * changes what the shopper is comparing. So before the clause is dropped whole
 * it is compacted to " With Sink". Nothing is compacted when the clause denies
 * the sink ("Sink Not Included").
 */
const SINK_RE = /\bsinks?\b/i;
const SINK_DENIED_RE = /\b(?:no|not|without|excluded|excluding|optional|separately)\b/i;

function compactSinkClause(clause) {
  const m = String(clause ?? "").match(SINK_RE);
  if (!m || SINK_DENIED_RE.test(clause)) return "";
  return ` With ${/s$/i.test(m[0]) ? "Sinks" : "Sink"}`;
}

/**
 * Every candidate name, shortest-last, in the removal order of round 39:
 * accessory clause → numeral quantity → filler → extra colours/materials →
 * unprotected descriptors from the middle outward → protected tokens only.
 *
 * The leading size (a `24"` prefix, or a `2' X 5'` run) is no longer the first
 * thing spent. Width is what a shopper picks a vanity, a doormat or a shower
 * door by, and dropping it left 24", 30" and 36" models under one title. Every
 * rung is now emitted with the size first and without it second, and
 * buildProductTitle only looks at the size-less candidates when no sized one
 * fits the hard cap.
 */
function shortenCandidates(name, productType = "") {
  const { dimensions, rest } = splitDimensionPrefix(name);
  const nouns = typeNouns(productType);
  const cut = clauseStart(rest, nouns);
  const productPart = (cut >= 0 ? rest.slice(0, cut) : rest).trim();
  const clause = cut >= 0 ? rest.slice(cut) : "";
  const sink = compactSinkClause(clause);

  const base = annotate(productPart, nouns);
  const out = [];
  // Round 40: each candidate records how many PROTECTED tokens it still carries,
  // and whether it comes from a "late" rung (after the accessory clause, the
  // filler and the extra colours/materials are already gone). A late candidate
  // inside the 65-char hard cap that keeps MORE protected tokens beats a shorter
  // one that gave up what the product does — see buildProductTitle.
  let late = false;
  const countProtected = (roles, keep) =>
    roles.reduce((n, r, i) => n + (keep[i] && KEPT_CLASSES.has(r) ? 1 : 0), 0);

  const push = (words, roles, keep, dims, cl, extra) => {
    const v = render(words, roles, keep, dims, cl);
    if (!v) return;
    out.push({
      name: v,
      dimensions: dims ? "" : dimensions,
      kept: countProtected(roles, keep),
      // Second colours and further materials still standing: not protected, but
      // what tells a "White And Silver" chair from a "White And Black" one.
      hues: roles.reduce((n, r, i) => n + (keep[i] && SPENDABLE_HUES.has(r) ? 1 : 0), 0),
      late,
      // Still carries every size the supplier states (true when it states none).
      sized: (!dimensions || Boolean(dims)) && roles.every((r, i) => r !== "size" || keep[i]),
      sink: SINK_RE.test(cl),
      ...extra,
    });
  };
  // One rung: size kept before size dropped, each clause form in the order given.
  const dimsForms = dimensions ? [dimensions, ""] : [""];
  const rung = (words, roles, keep, clauses, extra) => {
    for (const dims of dimsForms) for (const cl of clauses) push(words, roles, keep, dims, cl, extra);
  };
  const noClause = sink ? [sink, ""] : [""];

  const allKeep = () => base.words.map(() => true);

  // 1. the full name, then 2. the trailing accessory clause compacted or dropped
  rung(base.words, base.roles, allKeep(), clause && clause !== sink ? [clause, ...noClause] : noClause);

  // 3. numeral compaction of the quantity phrase
  let words = compactQuantity(base.words, base.roles);
  let roles = base.roles;
  rung(words, roles, allKeep(), noClause);

  // 4. filler that restates the category
  const keep = allKeep();
  for (let i = 0; i < roles.length; i++) if (roles[i] === "filler") keep[i] = false;
  rung(words, roles, [...keep], noClause);

  // 5a. colours after the first two, materials after the first
  late = true; // everything from here on is a "late" candidate
  for (let i = 0; i < roles.length; i++) {
    if (roles[i] === "colorExtra" || roles[i] === "materialExtra") keep[i] = false;
  }
  rung(words, roles, [...keep], noClause);


  // 5b. three or more colours collapse to "{first} Multicolor"
  if (base.colorCount >= 3) {
    const mcWords = [];
    const mcRoles = [];
    let inserted = false;
    for (let i = 0; i < words.length; i++) {
      if (roles[i] === "color2" || roles[i] === "colorExtra") continue;
      if (!keep[i] && roles[i] === "filler") continue;
      mcWords.push(words[i]);
      mcRoles.push(roles[i]);
      const nextRole = roles[i + 1];
      if (!inserted && roles[i] === "color1" && nextRole !== "color1") {
        mcWords.push("Multicolor");
        mcRoles.push("color1");
        inserted = true;
      }
    }
    // "{first} Multicolor" rewrites the colour run, so it is a last resort —
    // never preferred by the round-40 "richer candidate" rule.
    rung(mcWords, mcRoles, mcWords.map(() => true), [""], { mc: true });


  } else {
    for (let i = 0; i < roles.length; i++) if (roles[i] === "color2") keep[i] = false;
    rung(words, roles, [...keep], noClause);
  }

  // ---- Round 40, FIX 2. Shrink before giving anything up ------------------
  // What the product IS (functional + configuration) outranks an adjective, so
  // the ladder now spends the material PREFIX and the noun-phrase MODIFIER
  // first: "Italian Leather" → "Leather", "Corner Sectional" → "Sectional".

  // 6. the first material's prefix
  if (roles.some((r) => r === "matPrefix")) {
    for (let i = 0; i < roles.length; i++) if (roles[i] === "matPrefix") keep[i] = false;
    rung(words, roles, [...keep], noClause);
  }

  // 7. noun-phrase modifiers down to the head noun, EXCEPT recognised two-word
  // product types ("Bar Chairs", "Area Rug", "Floor Lamp"), kept whole.
  const nounIdx = [];
  for (let i = 0; i < roles.length; i++) if (roles[i] === "noun") nounIdx.push(i);
  if (nounIdx.length > 1) {
    const phrase = nounIdx.map((i) => words[i].toLowerCase().replace(/[^a-z]/g, "")).join(" ");
    if (!WHOLE_NOUN_TYPES.has(phrase)) {
      for (const i of nounIdx.slice(0, -1)) {
        // "Sham And Comforter" is two things, not a "Sham Comforter".
        if (base.typeNounAt.has(i) || isConjunction(words[i])) continue;
        keep[i] = false;
        // "Two Drawer Nightstand": the count goes with the drawer it counts.
        if (base.bound[i] >= 0) keep[base.bound[i]] = false;
      }
      // Round 41, FIX 3: a modifier that is a material word takes its prefix run
      // with it, so "Solid Wood Nightstand" can never become "Solid Nightstand".
      const first = nounIdx[0];
      const w0 = words[first].toLowerCase().replace(/[^a-z]/g, "");
      if (MATERIAL_WORD_RE.test(w0)) {
        for (let j = first - 1; j >= 0; j--) {
          const wj = words[j].toLowerCase().replace(/[^a-z-]/g, "");
          if (!MATERIAL_PREFIX_RE.test(wj) || MATERIAL_WORD_RE.test(wj)) break;
          keep[j] = false;
        }
      }
      rung(words, roles, [...keep], noClause);
    }
  }

  // 8. the second colour. Only a plain second colour is spent here (that already
  // happened in 5b); a COMPOUND second colour ("Light Gray", "Off White") is a
  // whole colour name and is only given up in the final sacrifice at step 12.


  // 10. Round 40, FIX 3. Remaining unprotected descriptors, RIGHT TO LEFT
  // starting next to the product noun: a modifier beside the noun carries the
  // least identity, while the subject words after the colour/material carry the
  // most ("Diving Man" must outlive "Dimensional").
  // 11. protected tokens only, in title order. Round 40: a protected token this
  // ladder already gave up (a material prefix, a noun modifier) is NOT resurrected.
  // `deep`: the name has started to give up its own descriptors.
  const spendDescriptors = (from) => {
    const k = [...from];
    for (let i = roles.length - 1; i >= 0; i--) {
      // A conjunction is not a descriptor: render() drops it with the word it
      // joined. Spent on its own, "Tan And Dark Gray" read "Tan Dark Gray".
      if (!k[i] || roles[i] === "size" || PROTECTED.has(roles[i]) || isConjunction(words[i])) continue;
      k[i] = false;
      // Its other half leaves in the same step (see `bound` in annotate).
      const other = base.bound[i];
      if (other >= 0 && roles[other] === "free") k[other] = false;
      rung(words, roles, [...k], noClause, { deep: true });
    }
    const onlyProtected = roles.map(
      (r, i) => Boolean(k[i]) && (PROTECTED.has(r) || r === "size" || isConjunction(words[i])),
    );
    rung(words, roles, onlyProtected, noClause, { deep: true });
    return onlyProtected;
  };

  // 9. the size runs (a leading "2' X 5'", a `27" X 54"` further in), each as one
  // group. Steps 10 and 11 are walked with them first, so a rug gives up its
  // descriptors before its size.
  if (roles.some((r) => r === "size")) {
    spendDescriptors(keep);
    for (let i = 0; i < roles.length; i++) if (roles[i] === "size") keep[i] = false;
    rung(words, roles, [...keep], noClause, { deep: true });
  }
  const onlyProtected = spendDescriptors(keep);


  // 12. overflow: even the protected-only fallback can exceed the 65-char hard
  // cap. Give up protected classes in increasing order of shopper value —
  // functional and configuration LAST before the colour, never the noun.
  const sacrifice = ["quoted", "functional", "config", "material1", "colorCompound", "color1", "bedSize", "qty"];
  const shrinking = [...onlyProtected];
  for (const role of sacrifice) {
    for (let i = 0; i < roles.length; i++) if (roles[i] === role) shrinking[i] = false;
    push(words, roles, [...shrinking], "", "", { deep: true });
  }


  return out;
}

/**
 * Rules (round 39):
 *  - name <= 49 chars  -> "{name} — LoftLoom"
 *  - name <= 60 chars  -> bare name (unchanged from round 32)
 *  - longer            -> the removal ladder above; the FIRST candidate that
 *    fits 60 wins, otherwise the first that fits the 65 hard max, otherwise the
 *    shortest candidate. Protected tokens (quantity, lead colour, first
 *    material, functional modifiers, product noun) are never removed or split.
 *  - a name that LEADS WITH A SIZE keeps it. The ladder is walked with the size
 *    first, and a name with an inch prefix (`36" `, which used to be dropped
 *    first) may use the whole 65 hard max — otherwise keeping the size would
 *    only push out the second colour or the accessory clause instead.
 *  - a name is not stripped of its own descriptors to save five characters:
 *    when nothing but a `deep` candidate fits 60, the first candidate inside the
 *    65 hard max that still has them all wins. Words that only mean something
 *    together (a size, a count and what it counts, "Down Alternative") are kept
 *    or dropped together — see `bound` in annotate().
 */
/** A leading size run in feet ("2' X 5'"), which splitDimensionPrefix (inches) leaves in the name. */
const LEADING_FEET_RE = /^\d+(?:\.\d+)?\s*(?:'|ft\.?|feet)(?:\s*[xX×]\s*\d+(?:\.\d+)?\s*(?:'|ft\.?|feet)?)*(?=\s|$)/i;

/** The first candidate that fits, by the round-39/40 rules; null when none fits the hard cap. */
function chooseCandidate(candidates, bareMax = TITLE_BARE_MAX) {
  // The FIRST candidate that fits 60 bare wins — the ladder is ordered from
  // "removed nothing" to "protected tokens only", so this is the most
  // descriptive name that fits. The suffix is only added when it also fits.
  const fits60 = candidates.find((c) => c.name.length <= bareMax);
  // Round 40, FIX 2: a LATE candidate inside the 65-char hard cap that keeps more
  // protected tokens than the 60-char pick wins — a name must not give up what
  // the product does ("Power Reclining U Shaped") to save five characters.
  const richer = candidates.find(
    (c) => c.late && !c.mc && c.name.length <= TITLE_HARD_MAX && (!fits60 || c.kept > fits60.kept),
  );
  // Same reasoning for "With Sink": worth five characters when nothing else is lost.
  const withSink =
    fits60 && !fits60.sink
      ? candidates.find((c) => c.sink && !c.mc && c.name.length <= TITLE_HARD_MAX && c.kept >= fits60.kept)
      : null;
  // And for the name's own descriptors: when only a `deep` candidate fits 60 —
  // one that has started to give up pattern, size or count words — a candidate
  // inside the hard cap that still has them all wins ("Six Panel", `27" X 54"`).
  const intact = fits60?.deep ? candidates.find((c) => !c.deep && !c.mc && c.name.length <= TITLE_HARD_MAX) : null;
  return intact ?? richer ?? withSink ?? fits60 ?? candidates.find((c) => c.name.length <= TITLE_HARD_MAX) ?? null;
}

export function buildProductTitle(rawTitle, productType = "") {
  const name = stripVendors(rawTitle) || "LoftLoom";
  if (name.length <= TITLE_WITH_SUFFIX_MAX) return { title: `${name}${SUFFIX}`, droppedDimensions: "" };
  if (name.length <= TITLE_BARE_MAX) return { title: name, droppedDimensions: "" };
  const { dimensions } = splitDimensionPrefix(name);
  const sellingSize = sellingSizePrefix(name);
  const sizedMax = sellingSize ? TITLE_HARD_MAX : TITLE_BARE_MAX;
  if (name.length <= sizedMax) return { title: name, droppedDimensions: "" };

  const all = shortenCandidates(name, productType);
  // A W x D x H triple goes first: only the candidates without it are looked at.
  const candidates = dimensions && !sellingSize ? all.filter((c) => c.dimensions) : all;
  // Size-less candidates are only looked at when no sized one fits the hard cap.
  const pick =
    (sellingSize || LEADING_FEET_RE.test(name) ? chooseCandidate(candidates.filter((c) => c.sized), sizedMax) : null) ??
    chooseCandidate(candidates) ??
    [...candidates].sort((a, b) => a.name.length - b.name.length)[0] ?? { name, dimensions: "" };


  if (pick.name.length <= TITLE_WITH_SUFFIX_MAX) {
    return { title: `${pick.name}${SUFFIX}`, droppedDimensions: pick.dimensions };
  }
  return { title: pick.name, droppedDimensions: pick.dimensions };
}


/**
 * Round 36. Same title, different product: append ONE qualifier (never a price
 * — a stale price in a <title> is a false claim). The qualifier itself is chosen
 * by the shared rules in scripts/title-qualifiers.mjs; this only composes it,
 * dropping the " — LoftLoom" suffix before the 65-char cap and giving the
 * qualifier up entirely rather than truncating a product name mid-phrase.
 *
 * When the pair does not fit, the name first gives up what costs it least (the
 * accessory clause, filler, "Solid" in "Solid Wood", a noun modifier) to make
 * room: the qualifier is the one thing that tells this page from its namesake.
 * It never costs a colour, a material, a descriptor ("California King"), a
 * protected token, the leading size or the mention of an included sink.
 */
export function buildQualifiedProductTitle(rawTitle, qualifier, productType = "") {
  const base = buildProductTitle(rawTitle, productType);
  if (!qualifier) return base;
  const bare = base.title.replace(SUFFIX, "");
  const tail = ` \u2014 ${qualifier}`;
  const compose = (name, droppedDimensions) => ({
    title: name.length + tail.length <= TITLE_WITH_SUFFIX_MAX ? `${name}${tail}${SUFFIX}` : `${name}${tail}`,
    droppedDimensions,
  });
  if (bare.length + tail.length <= TITLE_HARD_MAX) return compose(bare, base.droppedDimensions);

  const candidates = shortenCandidates(stripVendors(rawTitle) || "LoftLoom", productType);
  const picked = candidates.find((c) => c.name === bare) ?? candidates[0];
  const tighter = candidates.find(
    (c) =>
      c.name.length + tail.length <= TITLE_HARD_MAX &&
      !c.mc &&
      !c.deep &&
      c.kept >= (picked?.kept ?? 0) &&
      c.hues >= (picked?.hues ?? 0) &&
      (c.sized || !picked?.sized) &&
      (c.sink || !picked?.sink || SINK_RE.test(qualifier)),
  );
  return tighter ? compose(tighter.name, tighter.dimensions) : base;
}

/**
 * What the shortener removed from a supplier title, as phrases a title
 * qualifier may quote — the honest way to tell apart two products whose titles
 * only differ in what was cut. Round 36 could only look for these words in the
 * (stale) handle.
 *  - `features`: the accessory clause's parts, each WITH its own noun ("Ceramic
 *    Sink", "Four Doors"). A bare "Ceramic" on a wood vanity reads as the
 *    vanity's material.
 *  - `descriptors` / `materials`: runs dropped from the product phrase ("Low
 *    Back", "Executive", "Solid Wood"). Never a colour: a second colour beside
 *    the lead colour reads as a contradiction.
 *  - `phrase`: the product phrase itself, so a caller can tell the product's
 *    own words from the clause's.
 */
const QUOTE_SKIP_RE = /^(?:and|&|or|plus|with|in|a|an|the|of|for)$/i;

export function droppedTitlePhrases(rawTitle, builtBare, productType = "") {
  const name = stripVendors(rawTitle);
  const built = String(builtBare ?? "");
  const { rest } = splitDimensionPrefix(name);
  const nouns = typeNouns(productType);
  const cut = clauseStart(rest, nouns);
  const phrase = (cut >= 0 ? rest.slice(0, cut) : rest).trim();
  const out = { phrase, features: [], descriptors: [], materials: [] };
  if (!built || built === name) return out;

  const keptWords = new Set(built.toLowerCase().split(/[^a-z0-9%]+/).filter(Boolean));
  const isKept = (w) => {
    const k = w.toLowerCase().replace(/[^a-z0-9%]/g, "");
    return !k || keptWords.has(k) || keptWords.has(NUMBER_WORDS[k] ?? k);
  };
  const lowerOf = (w) => w.toLowerCase().replace(/[^a-z-]/g, "");
  const isColorish = (w) => COLOR_WORD_RE.test(lowerOf(w)) || COLOR_PREFIX_RE.test(lowerOf(w));
  // A tail must not start inside a pair that only means something whole
  // ("Off White", "Faux Leather", "Solid Wood").
  const opensPair = (w) =>
    COLOR_PREFIX_RE.test(lowerOf(w)) || MATERIAL_PREFIX_RE.test(lowerOf(w)) || /^(?:off|sky|navy|rose)$/i.test(w);

  const clause = cut >= 0 ? rest.slice(cut) : "";
  for (const piece of clause.split(/,|\s(?:with|featuring|including)\s/i)) {
    const words = piece.trim().split(/\s+/).filter(Boolean);
    while (words.length && QUOTE_SKIP_RE.test(words[0])) words.shift();
    if (!words.length || words.every(isKept)) continue;
    out.features.push(words.join(" "));
    const joined = words.findIndex((w) => /^(?:and|&)$/i.test(w));
    if (joined > 0) {
      // "Four Shelves And Six Drawers" is two features; "Blue And Beige Shade"
      // is one shade, and half its colour would misdescribe it.
      const left = words.slice(0, joined);
      const right = words.slice(joined + 1);
      if (!left.every(isColorish) && right.length > 1 && !right.some((w) => /^(?:and|&)$/i.test(w))) {
        if (left.length > 1) out.features.push(left.join(" "));
        out.features.push(right.join(" "));
      }
      continue;
    }
    for (let i = 1; i < words.length - 1; i++) {
      if (!opensPair(words[i - 1])) out.features.push(words.slice(i).join(" "));
    }
  }

  const { words, roles } = annotate(phrase, nouns);
  const kind = (r) =>
    r === "material1" || r === "materialExtra" || r === "matPrefix" ? "materials" : /^color|^qty$|^size$/.test(r) ? "" : "descriptors";
  let run = [];
  let runKind = "";
  const flush = () => {
    if (run.length && runKind) out[runKind].push(run.join(" "));
    run = [];
    runKind = "";
  };
  // A word that stands right before a colour, or is joined to one by "and", is
  // a colour name the vocabulary lacks ("Brick Red", "Chocolate and Black"):
  // quoted alone it would read as a second colour.
  const colourAt = (i) => /^color/.test(roles[i] ?? "");
  const joins = (i) => /^(?:and|&)$/i.test(words[i] ?? "");
  for (let i = 0; i < words.length; i++) {
    const k = kind(roles[i]);
    if ((colourAt(i) || (joins(i) && colourAt(i + 1))) && runKind === "descriptors") run = [];
    if (isKept(words[i]) || !k || QUOTE_SKIP_RE.test(words[i]) || (!run.length && joins(i - 1) && colourAt(i - 2))) {
      flush();
      continue;
    }
    if (k !== runKind) flush();
    runKind = k;
    run.push(words[i].replace(/,$/, ""));
  }
  flush();
  return out;
}

const COLOURS = [
  "antiqued brass", "antiqued gold", "brushed nickel", "matte black", "off white", "rose gold",
  "light grey", "dark grey", "espresso", "walnut", "oak", "teak", "ivory", "beige", "taupe",
  "charcoal", "black", "white", "grey", "gray", "brown", "blue", "navy", "green", "sage", "red",
  "burgundy", "orange", "yellow", "pink", "purple", "gold", "silver", "brass", "bronze", "nickel",
  "chrome", "copper", "cream", "natural", "multicolor", "multicolour", "clear",
];

const MATERIALS = [
  "solid manufactured wood", "manufactured wood", "engineered wood", "faux wood", "reclaimed wood",
  "faux leather", "genuine leather", "top grain leather", "boucle", "bouclé", "velvet", "linen",
  "chenille", "cotton", "wool", "jute", "sisal", "rattan", "wicker", "bamboo", "marble", "quartz",
  "granite", "ceramic", "porcelain", "glass", "acrylic", "resin", "concrete", "stainless steel",
  "steel", "iron", "aluminum", "aluminium", "metal", "brass", "wood", "leather", "polyester",
];

/**
 * "Linen Cabinet", "Linen Closet", "Linen Tower": the word names what the piece
 * stores. Read as a material it put "in linen beige" on 305 solid-wood cabinets
 * (src/lib/productSpecs.ts has the same rule for the on-page materials).
 */
const withoutStoredLinen = (text) => String(text ?? "").replace(/\blinen\s+(?=(?:cabinets?|closets?|towers?|storage)\b)/gi, "");

function findTerm(haystack, list) {
  const s = haystack.toLowerCase();
  for (const term of list) {
    if (new RegExp(`\\b${term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`).test(s)) return term;
  }
  return "";
}

/**
 * The material a description may name. "100% Cotton" outranks any fabric word
 * before it (a colour: "White And Linen 100% Cotton"), and a material the title
 * only states behind "Faux" / "Imitation" keeps that word: faux marble is not marble.
 */
function statedMaterial(text) {
  const s = withoutStoredLinen(text).toLowerCase();
  const pct = s.match(/\b100%\s+([a-z]+)\b/);
  if (pct && MATERIALS.includes(pct[1])) return pct[1];
  const term = findTerm(s, MATERIALS);
  if (!term) return "";
  const esc = term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  if (new RegExp(`(?<!\\b(?:faux|imitation|simulated)\\s)\\b${esc}\\b`).test(s)) return term;
  return `faux ${term}`;
}

function titleCase(s) {
  return s.replace(/\b([a-z])/g, (m) => m.toUpperCase());
}

/** First complete sentences of the supplier prose that still fit the budget.
 *  Exported so the prerendered product body reuses this one truncator. */
export function fittingSentences(prose, budget) {

  const text = String(prose ?? "").trim();
  if (!text || budget <= 0) return "";
  const sentences = text.match(/[^.!?]+[.!?]/g);
  if (!sentences) return "";
  let out = "";
  for (const raw of sentences) {
    const s = raw.trim();
    const next = out ? `${out} ${s}` : s;
    if (next.length > budget) break;
    out = next;
  }
  return out;
}

/**
 * `{Title}. {Type} in {material/colour}. ${price}. {In stock|Sold out}.
 *  Free shipping over $N.` then whole supplier sentences while they fit.
 * Never cuts mid-word: if the template alone overflows, the template ships alone.
 */
export function buildProductDescription(product, options = {}) {
  const {
    freeShippingThreshold = 120,
    differentiator = "",
    max = DESCRIPTION_MAX,
  } = options;

  const name = stripVendors(product.title) || "LoftLoom";
  const type = stripVendors(product.productType || "").trim();
  // Only the product's own words: a "… With White Legs" / "… With Gold Base"
  // clause describes a part, so its colour or material is not the product's.
  const haystack = `${name.split(/\s+with\s+/i)[0]} ${type}`;
  const material = statedMaterial(haystack);
  const colour = findTerm(haystack, COLOURS);
  const qualifier = [material, colour].filter(Boolean).slice(0, 2).join(" ");

  const price = Number(product.price ?? 0);
  const priceText = price > 0 ? `$${price.toFixed(2)}` : "";

  const tail = [];
  if (differentiator) tail.push(`${differentiator}.`);
  if (priceText) tail.push(`${priceText}.`);
  tail.push(product.availableForSale ? "In stock." : "Sold out.");
  tail.push(`Free U.S. shipping over $${freeShippingThreshold}.`);

  const typeClause = type
    ? qualifier
      ? `${titleCase(type)} in ${qualifier}.`
      : `${titleCase(type)}.`
    : "";

  // Longest lead-in that still keeps the whole template inside the budget, in
  // order of preference: full name, shortened page name, name without the type
  // clause. Whole phrases only — never a mid-word cut.
  const shortName = buildProductTitle(product.title, product.productType).title.replace(SUFFIX, "");
  const attempts = [
    [`${name}.`, typeClause],
    [`${shortName}.`, typeClause],
    [`${shortName}.`, ""],
  ];
  let template = "";
  for (const [lead, clause] of attempts) {
    template = [lead, clause, ...tail].filter(Boolean).join(" ");
    if (template.length <= max) break;
  }

  const room = max - template.length - 1;
  const extra = room > 0 ? fittingSentences(stripVendors(product.description), room) : "";
  return extra ? `${template} ${extra}` : template;
}

/** Colour / finish / size differentiator pulled from the title, for over-repeated groups. */
export function productDifferentiator(product) {
  const name = stripVendors(product.title);
  const { dimensions } = splitDimensionPrefix(name);
  const colour = findTerm(name, COLOURS);
  const material = statedMaterial(name);
  const bits = [];
  if (colour) bits.push(titleCase(colour));
  if (!colour && material) bits.push(titleCase(material));
  if (dimensions) bits.push(dimensions);
  return bits.join(", ");
}

const FAUX_WORD = "(?:faux|imitation|simulated)";

/**
 * What the "{Type} in {material} {colour}." clause must not say:
 *  - fauxAsReal: "in marble" where the product's own words only say "Faux
 *    Marble" (82 products; 116 with rattan, linen, concrete and wicker);
 *  - statedMaterialSwapped: another fabric than the one the title gives as
 *    "100% Cotton" ("in linen white" on three cotton pillows).
 */
export function descriptionDefects(product, description) {
  const out = { fauxAsReal: false, statedMaterialSwapped: false };
  const type = titleCase(stripVendors(product.productType || "").trim());
  if (!type) return out;
  const clause = String(description ?? "")
    .match(new RegExp(`\\. ${type.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")} in ([^.]+)\\.`))?.[1]
    ?.toLowerCase();
  if (!clause) return out;
  const phrase = withoutStoredLinen(stripVendors(product.title).split(/\s+with\s+/i)[0]).toLowerCase();
  const plain = (word, text) => new RegExp(`(?<!\\b${FAUX_WORD}\\s)\\b${word}\\b`).test(text);
  for (const m of phrase.matchAll(new RegExp(`\\b${FAUX_WORD}\\s+([a-z]+)\\b`, "g"))) {
    if (!plain(m[1], phrase) && plain(m[1], clause)) out.fauxAsReal = true;
  }
  const pct = phrase.match(/\b100%\s+([a-z]+)\b/);
  if (pct && MATERIALS.includes(pct[1])) {
    out.statedMaterialSwapped = !plain(pct[1], clause) && MATERIALS.some((mat) => mat !== pct[1] && plain(mat, clause));
  }
  return out;
}

/**
 * Round 39 build guard. The defects the round-39 shortener rebuild eliminated,
 * expressed as one reusable test so prerender-og.mjs asserts on exactly the
 * rules measured here. Only SHORTENED names are judged: two supplier titles
 * genuinely start with "In " ("In Rome I Gold Framed Wall Art") and are under
 * 60 chars, so they ship byte-identical and are not defects.
 */
const AUDIT_FRAGMENT_RE = /^(?:of|and|with|for|in|plus|or|blend)\b/i;
const AUDIT_QTY_RE = /\b(set|pair|pack|group|lot)\s+of\s+(\w+)\b/i;
const AUDIT_PIECE_RE = /\b(\w+)\s+(pieces?)\b/i;
const singularise = (w) =>
  /(?:ses|xes|hes)$/i.test(w) ? w.slice(0, -2) : /s$/i.test(w) && !/ss$/i.test(w) ? w.slice(0, -1) : w;

/**
 * Round 40, FIX 1. The first material stated in the product part (outside the
 * noun phrase, by the extended vocabulary) must be the material the built title
 * names. A built title that drops it while naming a LATER material has swapped
 * the material — a plastic chair claiming to be metal.
 */
export function firstMaterialOf(part) {
  // Uses the builder's OWN role assignment, so the audit and the shortener agree
  // about which word is the first material: a word that is a colour here
  // ("Black Silver and Bronze") is a colour, not a material, and a material word
  // inside the noun phrase ("Wall Mirror") is the noun.
  const { words, roles } = annotate(String(part ?? "").trim());
  for (let i = 0; i < words.length; i++) {
    if (roles[i] === "material1") return words[i].toLowerCase().replace(/[^a-z]/g, "");
  }
  return "";
}


/**
 * Round 41, FIX 1 + FIX 3 audits.
 * MEANING_PAIR_RE finds a meaning-bearing material pair ("Faux Leather",
 * "Manufactured Wood"); a built title must carry the whole pair or neither word.
 */
const MEANING_PAIR_RE = new RegExp(
  `\\b(${MEANING_PREFIXES})\\s+(${MATERIAL_WORDS.replace(/\|Mango Wood/, "")})\\b`,
  "gi",
);
const ANY_PREFIX_WORD_RE = new RegExp(`^(?:${MATERIAL_PREFIXES})$`, "i");
const ANY_MATERIAL_WORD_RE = new RegExp(`^(?:${MATERIAL_WORDS.replace(/\|Mango Wood/, "")})$`, "i");

/**
 * True when the shortener left MORE material prefixes standing without their
 * material than the supplier title already did. Counting, rather than a plain
 * presence test, keeps supplier strays out of the number: several titles read
 * "… Round Solid Coffee Table", where "Solid" is already bare.
 */
export function hasOrphanPrefix(built, part = "") {
  const split = (s) => String(s ?? "").split(/\s+/).map((w) => w.toLowerCase().replace(/[^a-z-]/g, ""));
  const followedByMaterial = (arr, i) => {
    let j = i + 1;
    while (j < arr.length && ANY_PREFIX_WORD_RE.test(arr[j]) && !ANY_MATERIAL_WORD_RE.test(arr[j])) j++;
    return j < arr.length && ANY_MATERIAL_WORD_RE.test(arr[j]);
  };
  const bare = (s) => {
    const arr = split(s);
    const counts = new Map();
    for (let i = 0; i < arr.length; i++) {
      if (!arr[i] || !ANY_PREFIX_WORD_RE.test(arr[i]) || ANY_MATERIAL_WORD_RE.test(arr[i])) continue;
      if (followedByMaterial(arr, i)) continue;
      counts.set(arr[i], (counts.get(arr[i]) ?? 0) + 1);
    }
    return counts;
  };
  const src = bare(part);
  for (const [word, n] of bare(built)) if (n > (src.get(word) ?? 0)) return true;
  return false;
}


/**
 * A size is one thing. `27" X 54"` cut to `27" X Bath Towels`, or to
 * `27" Bath Towels`, states a size the product does not have.
 *  - an "x" left with a measure on one side only — more often than the supplier
 *    title itself has one (a Roman "Collection X 2" is the supplier's);
 *  - a lone measure that the supplier title only states as one side of a pair.
 */
const AUDIT_UNIT = `(?:"|''|'|(?:inch(?:es)?|in\\.?|ft\\.?|feet|cm)(?![a-z]))`;
const AUDIT_MEASURE = `\\d+(?:\\.\\d+)?\\s*${AUDIT_UNIT}?`;
const AUDIT_SIZE_RUN_RE = new RegExp(`${AUDIT_MEASURE}(?:\\s*[x\u00d7]\\s*${AUDIT_MEASURE})+`, "gi");
const AUDIT_SIDE_RE = new RegExp(`\\d+(?:\\.\\d+)?\\s*${AUDIT_UNIT}`, "gi");
const AUDIT_DANGLING_X_RE = new RegExp(
  `(?:^|\\s)${AUDIT_MEASURE}\\s+[x\u00d7](?=\\s|$)|(?:^|\\s)[x\u00d7]\\s+\\d`,
  "gi",
);
const squash = (s) => s.replace(/\s+/g, "").toLowerCase();

export function hasBrokenSize(name, built) {
  const outside = (s) => s.replace(AUDIT_SIZE_RUN_RE, " ");
  const dangling = (s) => (outside(s).match(AUDIT_DANGLING_X_RE) ?? []).length;
  if (dangling(built) > dangling(name)) return true;
  const sides = new Set((name.match(AUDIT_SIZE_RUN_RE) ?? []).flatMap((run) => (run.match(AUDIT_SIDE_RE) ?? []).map(squash)));
  if (!sides.size) return false;
  const alone = new Set((outside(name).match(AUDIT_SIDE_RE) ?? []).map(squash));
  return (outside(built).match(AUDIT_SIDE_RE) ?? []).map(squash).some((m) => sides.has(m) && !alone.has(m));
}

/**
 * A count left without the word it counts: "Six Panel Outdoor Privacy Screen"
 * shortened to "Six Outdoor Privacy Screen", "Three Leg Console Table" to a
 * "Three Console Table". Every count the built title keeps must still be
 * followed by a word the supplier title puts after it. "Set Of Four" and
 * "Service For Four" are complete as they stand.
 */
const AUDIT_COUNT_RE = /^(?:one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve|single|double|dual|triple)$/;

export function hasOrphanCount(name, built) {
  const keys = (s) => s.split(/\s+/).map((w) => w.toLowerCase().replace(/[^a-z0-9]/g, ""));
  const counts = (w, i, all) => AUDIT_COUNT_RE.test(w) && all[i - 1] !== "of" && all[i - 1] !== "for";
  const followers = new Map();
  keys(name).forEach((w, i, all) => {
    if (!counts(w, i, all)) return;
    if (!followers.has(w)) followers.set(w, new Set());
    followers.get(w).add(all[i + 1] ?? "");
  });
  if (!followers.size) return false;
  return keys(built).some((w, i, all) => counts(w, i, all) && followers.has(w) && !followers.get(w).has(all[i + 1] ?? ""));
}

export function titleDefects(rawTitle, builtBare, productType = "") {
  const name = stripVendors(rawTitle);
  const built = String(builtBare ?? "");
  const out = {
    fragment: false, quantity: false, noun: false, compoundColor: false,
    materialSwapped: false, materialPrefixDropped: false, orphanPrefix: false,
    typeNoun: false, sizeLost: false, sinkLost: false, brokenSize: false, countOrphaned: false,
    statedMaterialLost: false,
  };
  if (built === name) return out; // untouched supplier title
  // "100% Cotton" is the supplier stating the material. A title that keeps the
  // "100%" keeps its word, and one that cannot fit the pair names no other
  // fabric in its place ("… White And Linen 100% Cotton … Pillow" shipped as
  // "… Linen 100% Striped Pillow").
  const pct = name.match(/\b100%\s+([A-Za-z]+)/);
  if (pct && MATERIAL_WORD_RE.test(pct[1])) {
    const stated = pct[1].toLowerCase();
    const whole = new RegExp(`\\b100%\\s+${stated}\\b`, "i").test(built);
    const others = (built.match(MATERIAL_SCAN_RE) ?? []).some((w) => w.toLowerCase() !== stated);
    out.statedMaterialLost = !whole && (built.includes("100%") || others);
  }
  out.brokenSize = hasBrokenSize(name, built);
  out.countOrphaned = hasOrphanCount(name, built);
  // The same clause boundary the builder used, so both judge one product phrase.
  const nouns = typeNouns(productType);
  const cut = clauseStart(name, nouns);
  const part = (cut >= 0 ? name.slice(0, cut) : name).trim();
  const low = built.toLowerCase();

  // The supplier type's noun, when the product phrase states it, is in the title.
  const partWords = part.split(/\s+/);
  const builtWords = built.split(/\s+/);
  out.typeNoun =
    partWords.some((_w, i) => isTypeNoun(partWords, i, nouns)) &&
    !builtWords.some((_w, i) => isTypeNoun(builtWords, i, nouns));
  // A leading size and an included sink are the last things to go (counted, not
  // forbidden: past the 65-char cap the protected tokens still come first).
  const lead = sellingSizePrefix(name) || (name.match(LEADING_FEET_RE)?.[0] ?? "");
  out.sizeLost = Boolean(lead) && !built.startsWith(lead);
  out.sinkLost = Boolean(cut >= 0 && compactSinkClause(name.slice(cut))) && !SINK_RE.test(built);

  out.fragment = AUDIT_FRAGMENT_RE.test(built);

  const q = part.match(AUDIT_QTY_RE);
  if (q) {
    const n = NUMBER_WORDS[q[2].toLowerCase()] ?? (/^\d+$/.test(q[2]) ? q[2] : null);
    out.quantity =
      !low.includes(q[1].toLowerCase()) ||
      (n != null && !low.includes(String(n)) && !low.includes(q[2].toLowerCase()));
  }
  const pc = part.match(AUDIT_PIECE_RE);
  if (!out.quantity && pc && (NUMBER_WORDS[pc[1].toLowerCase()] || /^\d+$/.test(pc[1]))) {
    const n = NUMBER_WORDS[pc[1].toLowerCase()] ?? pc[1];
    out.quantity = !/\bpiece/i.test(built) || (!low.includes(String(n)) && !low.includes(pc[1].toLowerCase()));
  }

  const last = part.split(/\s+/).filter(Boolean).pop() ?? "";
  out.noun = Boolean(last) && !low.includes(singularise(last).toLowerCase());

  const lowPart = ` ${part.toLowerCase()} `;
  for (const cc of COMPOUND_COLORS) {
    if (lowPart.includes(` ${cc} `) && !low.includes(cc)) out.compoundColor = true;
  }

  const firstMat = firstMaterialOf(part);
  if (firstMat && !low.includes(firstMat)) {
    const others = (built.match(MATERIAL_SCAN_RE) ?? []).map((w) => w.toLowerCase());
    out.materialSwapped = others.some((w) => w !== firstMat);
  }

  // Round 41, FIX 1: the pair is kept whole or dropped whole. Counted, not
  // presence-tested, so a SECOND genuine material of the same word does not
  // read as a dropped prefix ("Solid Wood And Solid Manufactured Wood").
  const unpaired = (s, p, mat) => {
    const re = new RegExp(`(\\b${p}\\s+)?\\b${mat}\\b`, "gi");
    let n = 0, m2;
    while ((m2 = re.exec(s))) if (!m2[1]) n++;
    return n;
  };
  MEANING_PAIR_RE.lastIndex = 0;
  let pair;
  while ((pair = MEANING_PAIR_RE.exec(part))) {
    const p = pair[1].toLowerCase();
    const mat = pair[2].toLowerCase();
    if (unpaired(built, p, mat) > unpaired(part, p, mat)) out.materialPrefixDropped = true;
  }


  // Round 41, FIX 3: no prefix left standing without its material.
  // compared against the WHOLE supplier name (an accessory clause may legitimately
  // carry "Mirrored Shelf", which the built title keeps verbatim).
  out.orphanPrefix = hasOrphanPrefix(built, name);
  return out;
}



/**
 * The same question for the SHORT display names — cards, search, rails, cart
 * toasts (cleanProductTitle in src/lib/productTitle.ts): does the name still
 * call the product what the supplier calls it? Those names are recomposed from
 * a noun vocabulary, and a noun the vocabulary knows is not always the product:
 * a "Lamp Shade" was sold on a card as a "Lamp", a "Rug Pad" as a "Rug", framed
 * wall art as a "Shelf". `title` is the supplier title as the display cleans it.
 *  - typeNoun: the supplier type's noun, stated in the product phrase, is gone
 *    ("Bar Chairs" shown as "Chairs" counts: both words are the type);
 *  - headNoun: the product phrase's own last noun is gone.
 */
const TRAILING_COUNT_RE = /^(?:sets?|pairs?|packs?|collection)$/i;

export function displayNameDefects(title, productType, displayName) {
  const out = { typeNoun: false, headNoun: false };
  const name = stripVendors(title);
  const nouns = typeNouns(productType);
  const cut = clauseStart(name, nouns);
  const phrase = (cut >= 0 ? name.slice(0, cut) : name).split(/\s+/).filter((w) => /[a-z]/i.test(w));
  // A closing "Set" / "Pack" is a count — unless it IS the type ("Dining Sets").
  while (phrase.length > 1 && TRAILING_COUNT_RE.test(phrase[phrase.length - 1]) && !isTypeNoun(phrase, phrase.length - 1, nouns)) {
    phrase.pop();
  }
  const shown = String(displayName ?? "").split(/\s+/).map(nounStem).filter(Boolean);
  if (!phrase.length || !shown.length) return out;
  // "Armchair" shows "chair", "Nightstand" shows "stand".
  const shows = (stem) => shown.some((w) => w === stem || w.endsWith(stem));

  for (let i = 0; i < phrase.length && !out.typeNoun; i++) {
    const span = isTypeNoun(phrase, i, nouns);
    if (!span) continue;
    const stems = phrase.slice(i - span + 1, i + 1).map(nounStem);
    out.typeNoun = !shows(stems.join("")) && !stems.every(shows);
  }
  out.headNoun = !shows(nounStem(phrase[phrase.length - 1]));
  return out;
}

const BULB_COUNT_RE =
  /(?<!\b(?:set|pair|pack)\s+of\s+)\b(?:one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve|\d{1,2})[\s-]*lights?\s+([a-z]+)\b(?=.*\b(?:lights?|lamps?|sconces?|chandeliers?|pendants?|lanterns?|fixtures?|candle\s+holders?)\b)/i;

/**
 * What a short display name must not CLAIM. It may say less than the supplier
 * title, never something else:
 *  - bulbAsColour: "Terra 2-Light Gray Sconce" shown as "Light Gray Sconce" —
 *    the bulb count read as the colour's "Light" (78 light fittings);
 *  - blendDropped: "Silk Blend Loveseat" shown as "Silk", "Leather Match" as
 *    "Leather" (217 products);
 *  - statedMaterialSwapped: "… White And Linen 100% Cotton … Pillow" shown as
 *    a "Linen Pillow".
 * `title` is the supplier title as the display cleans it.
 */
export function displayClaimDefects(title, displayName) {
  const out = { bulbAsColour: false, blendDropped: false, statedMaterialSwapped: false };
  const name = stripVendors(title);
  const shown = String(displayName ?? "");
  if (!name || !shown) return out;

  const bulb = name.match(BULB_COUNT_RE);
  out.bulbAsColour = Boolean(bulb) && new RegExp(`^light\\s+${bulb[1]}\\b`, "i").test(shown);

  const phrase = name.split(/,|\s(?:with|featuring|including)\s/i)[0];
  for (const m of phrase.matchAll(/\b([A-Za-z]+)\s+(Blend|Match)\b/gi)) {
    if (!MATERIAL_WORD_RE.test(m[1])) continue;
    // "Leather And Leather Match" states plain leather too.
    if (new RegExp(`\\b${m[1]}\\b(?!\\s+(?:blend|match)\\b)`, "i").test(phrase)) continue;
    const kept = new RegExp(`\\b${m[1]}\\s+${m[2]}\\b`, "i").test(shown);
    if (!kept && new RegExp(`\\b${m[1]}\\b`, "i").test(shown)) out.blendDropped = true;
  }

  const pct = name.match(/\b100%\s+([A-Za-z]+)/);
  if (pct && MATERIAL_WORD_RE.test(pct[1])) {
    const stated = pct[1].toLowerCase();
    const named = (shown.match(MATERIAL_SCAN_RE) ?? []).map((w) => w.toLowerCase());
    out.statedMaterialSwapped = named.length > 0 && !named.includes(stated);
  }
  return out;
}

/** Round 40 debugging aid: the full removal ladder for one name. */
export const debugCandidates = shortenCandidates;
